  import React, { useState, useRef, useEffect } from "react";
  import { NavLink } from "react-router-dom";
  import {
    getAllUserBooking,
    uploadTransitSingleFile,
  } from "../Services/userService";
  import { RoutesPaths, logisticTypes, STATUS_OPTIONS } from "../Router/constant";
  import Skeleton from "../Components/SkeletonLoader";
  import ConfirmationModal from "../Components/ConfirmationModal";
  import { updateBookingStatus } from "../Services/bookingService";
  import { toast } from "react-toastify";
  import {
    generateDeliveryOrderPdf,
    generateWorkOrderPdf,
  } from "../Utils/pdfGenerator";

  export default function Bookingmanagement() {
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");

    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);

    const [bookingUserData, setBookingUserData] = useState(null);

    const [limit] = useState(10);
    const [offset, setOffset] = useState(1);

    const [activeProgress, setActiveProgress] = useState(null);

    const [confirmation, setConfirmation] = useState({
      show: false,
      orderId: null,
      statusValue: "",
    });

    const [activeDropdown, setActiveDropdown] = useState(null);
    const [loading, setLoading] = useState(true);

    /*
    * FILTER STATE
    *
    * As soon as any value changes,
    * useEffect will call API.
    */
    const [filters, setFilters] = useState({
      logisticsType: "",
      status: "",
      fromDate: "",
      toDate: "",
    });

    const userRole = localStorage.getItem("userRole");

    /*
    * ==========================================
    * STATUS DROPDOWN
    * ==========================================
    */

    const toggleDropdown = (id) => {
      setActiveDropdown(activeDropdown === id ? null : id);
    };

    /*
    * ==========================================
    * FILE UPLOAD
    * ==========================================
    */

    const handleFileChange = async (event) => {
      const file = event.target.files[0];

      if (!file) return;

      setConfirmation((prev) => ({
        ...prev,
        attachment: file,
      }));

      try {
        const formData = new FormData();

        formData.append("file", file);

        const response = await uploadTransitSingleFile(formData);

        console.log(response, "response");

        if (response?.data?.status === true) {
          const fileName = response.data.data.fileName;

          setConfirmation((prev) => ({
            ...prev,
            uploadedFileName: fileName,
          }));
        } else {
          toast.error("File upload failed");
        }
      } catch (error) {
        console.error("File upload error:", error);

        toast.error("File upload error");
      }
    };

    /*
    * ==========================================
    * FETCH BOOKING
    * ==========================================
    */

    const fetchBookingData = async () => {
      setLoading(true);

      const params = {
        offset,
        limit,
        sort: sortOrder,

        status: filters.status,
        logisticsType: filters.logisticsType,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      try {
        /*
        * Existing partner condition
        */
        if (userRole === "partner") {
          params.isPartner = true;
          params.partnerId = localStorage.getItem("partnerId");
        }

        const bookingResp = await getAllUserBooking(params);

        setBookingUserData(bookingResp?.data);
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        setLoading(false);
      }
    };

    /*
    * API CALL:
    *
    * - pagination change
    * - sort change
    * - logistics filter change
    * - status change
    * - from date change
    * - to date change
    */
    useEffect(() => {
      fetchBookingData();
    }, [
      offset,
      sortOrder,
      filters.status,
      filters.logisticsType,
      filters.fromDate,
      filters.toDate,
    ]);

    /*
    * ==========================================
    * FILTER CHANGE
    * ==========================================
    */

    const handleFilterChange = (event) => {
      const { name, value } = event.target;

      /*
      * Date validation
      */
      if (
        name === "fromDate" &&
        filters.toDate &&
        value &&
        value > filters.toDate
      ) {
        toast.error("From date cannot be greater than To date");

        return;
      }

      if (
        name === "toDate" &&
        filters.fromDate &&
        value &&
        value < filters.fromDate
      ) {
        toast.error("To date cannot be less than From date");

        return;
      }

      /*
      * Reset pagination when filter changes
      */
      setOffset(1);

      /*
      * Updating this state automatically
      * triggers useEffect API call.
      */
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    /*
    * ==========================================
    * CLEAR FILTER
    * ==========================================
    */

    const handleClearFilters = () => {
      setOffset(1);

      setFilters({
        logisticsType: "",
        status: "",
        fromDate: "",
        toDate: "",
      });
    };

    /*
    * ==========================================
    * CLICK OUTSIDE
    * ==========================================
    */

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setActiveDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    /*
    * ==========================================
    * BOOKING DATA
    * ==========================================
    */

    const orders = bookingUserData?.data?.orders || [];

    const count = bookingUserData?.data?.count || 0;

    const totalPages = Math.ceil(count / limit);

    const currentPage = offset;

    /*
    * ==========================================
    * PAGINATION
    * ==========================================
    */

    const generatePagination = (currentPage, totalPages) => {
      const pages = [];

      if (totalPages <= 4) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 2) {
          pages.push(1, 2, "...", totalPages - 1, totalPages);
        } else if (currentPage <= 4) {
          pages.push(3, 4, "...", totalPages - 1, totalPages);
        } else if (currentPage >= totalPages - 2) {
          for (let i = totalPages - 2; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(
            currentPage,
            currentPage + 1,
            "...",
            totalPages - 1,
            totalPages,
          );
        }
      }

      return pages;
    };

    /*
    * ==========================================
    * HANDLE STATUS
    * ==========================================
    */

    const handleStatusClick = (orderId, statusValue) => {
      setConfirmation({
        show: true,
        orderId,
        statusValue,

        customStatus:
          statusValue.toLowerCase() === "in transit" ? "" : null,

        messageText:
          statusValue.toLowerCase() === "in transit" ? "" : null,
      });

      setActiveDropdown(null);
    };

    /*
    * ==========================================
    * CONFIRM STATUS
    * ==========================================
    */

    const handleConfirm = async () => {
      const { orderId, statusValue, customStatus, messageText } = confirmation;

      let payload;

      if (statusValue.toLowerCase() === "in transit") {
        payload = {
          status: "in transit",
          customStatus: customStatus || "",
          inTransitMessage: messageText || "",
          attachment: confirmation.uploadedFileName || "",
        };
      } else {
        payload = {
          status: statusValue,
        };
      }

      try {
        setConfirmLoading(true);

        const response = await updateBookingStatus(orderId, payload);

        if (response?.data?.status) {
          toast.success(response?.data?.data?.message);

          setActiveDropdown(null);

          setConfirmation({
            show: false,
            orderId: null,
            statusValue: "",
            customStatus: "",
            messageText: "",
            attachment: "",
            uploadedFileName: "",
          });

          /*
          * Current filters remain applied
          */
          fetchBookingData();
        } else {
          toast.error(
            response?.data?.data?.message || "Failed to update order status.",
          );
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.data?.message ||
            "An unexpected error occurred.",
        );
      } finally {
        setConfirmLoading(false);
      }
    };

    /*
    * ==========================================
    * FORMAT LOGISTICS
    * ==========================================
    */

    const formatLogisticsType = (type = "") =>
      String(type)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/^./, (char) => char.toUpperCase());

    /*
    * ==========================================
    * STATUS BADGE
    * ==========================================
    */

    const formatStatusBadge = (status) => {
      const map = {
        pending: "Pending",
        completed: "Completed",
        inprogress: "In-progress",
        "in progress": "In-progress",

        "order placed": "Order Placed",
        "submitted for quote": "Submitted for Quote",

        "quote in place and sent to client for approval":
          "Quote Sent to Client",

        "quote approved by client": "Quote Approved",

        "quote rejected by client": "Quote Rejected",

        "order accepted": "Order Accepted",

        "product shipped": "Product Shipped",

        "in-transit": "In-Transit",

        "in transit": "In-Transit",

        delivered: "Delivered",
      };

      return map[String(status).toLowerCase()] || status;
    };

    /*
    * ==========================================
    * FORMAT PROGRESS
    * ==========================================
    */

    const formatStatus = (status = "") => {
      const map = {
        "order placed": "Order placed",

        "submitted for quote": "Submitted for Quote",

        "quote in place and sent to client for approval":
          "Quote Sent to Client for Approval",

        "quote approved by client":
          "Quote Approved by Client",

        "quote rejected by client":
          "Quote Rejected by Client",

        "order accepted": "Order Accepted",

        "product shipped": "Product Shipped",

        "in transit": "In-transit",

        "in-transit": "In-transit",

        delivered: "Delivered",
      };

      return map[status.toLowerCase()] || status;
    };

    /*
    * ==========================================
    * DOWNLOAD WORK ORDER
    * ==========================================
    */

    const handleDownloadPdf = async (order) => {
      try {
        await generateWorkOrderPdf(order);
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      }
    };

    /*
    * ==========================================
    * DOWNLOAD DELIVERY ORDER
    * ==========================================
    */

    const handleDownloadDeliveryOrder = async (order) => {
      try {
        await generateDeliveryOrderPdf(order);
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      }
    };

    /*
    * Check whether any filter is active
    */
    const isFilterActive =
      filters.logisticsType ||
      filters.status ||
      filters.fromDate ||
      filters.toDate;

    return (
      <div className="container-fluid">
        <div className="row dashboardSection">

          {/* Breadcrumb */}

          <div className="col-md-12">
            <div className="logisticsMenu">
              <p>
                <NavLink to={RoutesPaths.DASHBOARD}>
                  <img src="/assets/img/homeIcon.svg" alt="" />
                </NavLink>
              </p>

              <img src="/assets/img/chevron-right.svg" alt="" />

              <p>
                <NavLink
                  to={RoutesPaths.BOOKINGMANAGEMENT}
                  style={{ cursor: "pointer" }}
                >
                  Booking management
                </NavLink>
              </p>
            </div>
          </div>

          {/* ========================================= */}
          {/* BOOKING SUMMARY CARDS */}
          {/* ========================================= */}

          <div className="col-md-3">
            <div className="bookingManagement">
              <div>
                {loading ? (
                  <>
                    <h6 className="m-0">
                      <Skeleton width={60} />
                    </h6>

                    <p className="m-0">
                      <Skeleton width={100} />
                    </p>
                  </>
                ) : (
                  <>
                    <h6 className="m-0">
                      {count}
                    </h6>

                    <p className="m-0">
                      Total booking
                    </p>
                  </>
                )}
              </div>

              <img
                src="/assets/img/totalBooking.png"
                alt=""
                className="img-fluid"
              />
            </div>
          </div>

          {/* Pending */}

          <div className="col-md-3">
            <div className="bookingManagement">
              <div>
                {loading ? (
                  <>
                    <h6 className="m-0">
                      <Skeleton width={60} />
                    </h6>

                    <p className="m-0">
                      <Skeleton width={100} />
                    </p>
                  </>
                ) : (
                  <>
                    <h6 className="m-0">
                      {bookingUserData?.data?.records?.find(
                        (r) => r.status === "pending",
                      )?.count || 0}
                    </h6>

                    <p className="m-0">
                      Pending booking
                    </p>
                  </>
                )}
              </div>

              <img
                src="/assets/img/unpaid.jpg"
                alt=""
                className="img-fluid"
              />
            </div>
          </div>

          {/* In Progress */}

          <div className="col-md-3">
            <div className="bookingManagement">
              <div>
                {loading ? (
                  <>
                    <h6 className="m-0">
                      <Skeleton width={60} />
                    </h6>

                    <p className="m-0">
                      <Skeleton width={100} />
                    </p>
                  </>
                ) : (
                  <>
                    <h6 className="m-0">
                      {bookingUserData?.data?.records?.find(
                        (r) => r.status === "inprogress",
                      )?.count || 0}
                    </h6>

                    <p className="m-0">
                      In-progress booking
                    </p>
                  </>
                )}
              </div>

              <img
                src="/assets/img/checkin.png"
                alt=""
                className="img-fluid"
              />
            </div>
          </div>

          {/* Completed */}

          <div className="col-md-3">
            <div className="bookingManagement">
              <div>
                {loading ? (
                  <>
                    <h6 className="m-0">
                      <Skeleton width={60} />
                    </h6>

                    <p className="m-0">
                      <Skeleton width={120} />
                    </p>
                  </>
                ) : (
                  <>
                    <h6 className="m-0">
                      {bookingUserData?.data?.records?.find(
                        (r) => r.status === "completed",
                      )?.count || 0}
                    </h6>

                    <p className="m-0">
                      Completed booking
                    </p>
                  </>
                )}
              </div>

              <img
                src="/assets/img/checkout.png"
                alt=""
                className="img-fluid"
              />
            </div>
          </div>

          {/* ========================================= */}
          {/* BOOKING TABLE */}
          {/* ========================================= */}

          <div className="col-md-12">
            <div className="bookingDetailsMain bookingManagementMain">
              <div className="bookingDetailsOuter">

                {/* ========================================= */}
                {/* TITLE + FILTERS + CREATE BUTTON */}
                {/* EVERYTHING INSIDE bookingDetailsAlign */}
                {/* ========================================= */}
                <div className="bookingDetailsAlign pb-0">
                  <h6
                    className="bookingDetailsTitle p-0"
                  >
                    Booking details
                  </h6>
                   {userRole === "admin" ? (
                      <NavLink
                        to={`${RoutesPaths.ADDPUBLICORDER}`}
                        style={{
                          textDecoration: "none",
                        }}
                      >
                        <button
                          className="createOrderBtnDetail"
                          style={{
                            height: "40px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          + Create order for new user
                        </button>
                      </NavLink>
                    ) : (
                      ""
                    )}
                </div>
                <div
                  className="bookingDetailsAlign"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: "15px",
                    flexWrap: "wrap",
                    paddingTop: "10px",
                  }}
                >

                  <div
                    className="bookingManagementFilters"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        Logistics type
                      </label>

                      <select
                        name="logisticsType"
                        value={filters.logisticsType}
                        onChange={handleFilterChange}
                        style={{
                          height: "40px",
                          minWidth: "165px",
                          padding: "0 34px 0 12px",
                          border: "1px solid #e1e5ea",
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          color: "#333",
                          fontSize: "13px",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">
                          All logistics
                        </option>

                        {Object.entries(logisticTypes || {}).map(
                          ([value, label]) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {formatLogisticsType(label)}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    {/* ================================ */}
                    {/* STATUS */}
                    {/* ================================ */}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        Status
                      </label>

                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        style={{
                          height: "40px",
                          minWidth: "145px",
                          padding: "0 34px 0 12px",
                          border: "1px solid #e1e5ea",
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          color: "#333",
                          fontSize: "13px",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">
                          All status
                        </option>

                        <option value="pending">
                          Pending
                        </option>

                        <option value="inprogress">
                          In-progress
                        </option>

                        <option value="completed">
                          Completed
                        </option>
                      </select>
                    </div>

                    {/* ================================ */}
                    {/* FROM DATE */}
                    {/* ================================ */}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        From date
                      </label>

                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        max={filters.toDate || undefined}
                        onChange={handleFilterChange}
                        style={{
                          height: "40px",
                          width: "150px",
                          padding: "0 10px",
                          border: "1px solid #e1e5ea",
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          color: "#333",
                          fontSize: "13px",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      />
                    </div>

                    {/* ================================ */}
                    {/* TO DATE */}
                    {/* ================================ */}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        To date
                      </label>

                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        min={filters.fromDate || undefined}
                        onChange={handleFilterChange}
                        style={{
                          height: "40px",
                          width: "150px",
                          padding: "0 10px",
                          border: "1px solid #e1e5ea",
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          color: "#333",
                          fontSize: "13px",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      />
                    </div>

                    {/* ================================ */}
                    {/* CLEAR FILTER */}
                    {/* ================================ */}

                    {isFilterActive ? (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        style={{
                          height: "40px",
                          padding: "0 15px",
                          border: "1px solid #d9dde3",
                          borderRadius: "6px",
                          background: "#fff",
                          color: "#555",
                          fontSize: "13px",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    ) : null}

                    {/* ================================ */}
                    {/* CREATE ORDER */}
                    {/* ================================ */}

                  </div>
                </div>
                <div className="TableOverFlow bookingManagementTable">
                  <div className="bookingContent bookingDetailsHeader">

                    <div className="m-0 orderIdWidth">
                      Order id
                    </div>

                    {/* <div className="m-0 userNameWidth">
                      Customer name
                    </div> */}

                    <div className="m-0 logisticswidth">
                      Logistics type
                    </div>

                    {/* Date Sort */}

                    <div
                      className="m-0 statusWidth"
                      onClick={() => {
                        const newSort =
                          sortOrder === "asc"
                            ? "desc"
                            : "asc";

                        /*
                        * Keep filters,
                        * just change sorting.
                        */
                        setOffset(1);

                        setSortOrder(newSort);
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      Date

                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          fontSize: "10px",
                          lineHeight: "10px",
                        }}
                      >
                        <span
                          style={{
                            color:
                              sortOrder === "asc"
                                ? "#000"
                                : "#ccc",
                          }}
                        >
                          ▲
                        </span>

                        <span
                          style={{
                            color:
                              sortOrder === "desc"
                                ? "#000"
                                : "#ccc",
                          }}
                        >
                          ▼
                        </span>
                      </span>
                    </div>

                    <div className="m-0 statusWidth">
                      Order progress
                    </div>

                    <div className="m-0 statusWidth">
                      Status
                    </div>

                    <div
                      className="m-0 logisticsActionWidth"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      Action
                    </div>

                  </div>

                  {/* ========================================= */}
                  {/* TABLE BODY */}
                  {/* ========================================= */}

                  <div>
                    {loading ? (
                      [...Array(5)].map((_, i) => (
                        <div
                          className="bookingContent bookingContentData"
                          key={i}
                        >
                          <div className="m-0 orderIdWidth">
                            <Skeleton />
                          </div>

                          <div className="m-0 userNameWidth">
                            <Skeleton />
                          </div>

                          <div className="m-0 logisticswidth">
                            <Skeleton />
                          </div>

                          <div className="statusWidth">
                            <Skeleton />
                          </div>

                          <div className="statusWidth">
                            <Skeleton />
                          </div>

                          <div className="m-0 logisticswidth">
                            <Skeleton />
                          </div>

                          <div className="m-0 bookingActionwidth">
                            <Skeleton />
                          </div>
                        </div>
                      ))
                    ) : orders.length > 0 ? (
                      orders.map((order, index) => {
                        const isDelivered =
                          order?.orderHistory?.some(
                            (item) =>
                              item.status === "delivered" &&
                              item.completed,
                          );

                        return (
                          <div
                            className="bookingContent bookingContentData"
                            key={`${order._id}-${index}`}
                          >

                            {/* Order ID */}

                            <div
                              className="m-0 orderIdWidth orderIdActive"
                              onClick={() => handleDownloadPdf(order)}
                              style={{
                                cursor: "pointer",
                              }}
                            >
                              #{order.orderId}
                            </div>

                            {/* Customer Name */}

                            {/* <div className="m-0 userNameWidth">
                              {order.name || "N/A"}
                            </div> */}

                            {/* Logistics */}

                            <div className="m-0 logisticswidth">
                              {formatLogisticsType(
                                logisticTypes[
                                  order.logisticsType
                                ],
                              ) || "N/A"}
                            </div>

                            {/* Date */}

                            <div className="m-0 statusWidth">
                              {new Date(
                                order.createdAt,
                              ).toLocaleDateString(
                                "en-GB",
                              )}
                            </div>

                            {/* Order Progress */}

                            <div className="m-0 statusWidth orderProgress">
                              {(() => {
                                const progressList = [
                                  ...new Set(
                                    (
                                      order.orderHistory ||
                                      []
                                    )
                                      .filter(
                                        (h) =>
                                          h.completed,
                                      )
                                      .map((h) => {
                                        if (
                                          h.status ===
                                          "product shipped"
                                        ) {
                                          return "in transit";
                                        }

                                        return h.status;
                                      }),
                                  ),
                                ].map((status) =>
                                  formatStatus(status),
                                );

                                if (
                                  progressList.length ===
                                  0
                                ) {
                                  return "-";
                                }

                                const latest =
                                  progressList[
                                    progressList.length -
                                      1
                                  ];

                                return (
                                  <>
                                    {latest}

                                    {progressList.length >
                                      1 && (
                                      <span
                                        className="moreText clickable"
                                        onClick={() =>
                                          setActiveProgress(
                                            progressList,
                                          )
                                        }
                                      >
                                        {" "}
                                        +
                                        {progressList.length -
                                          1}{" "}
                                        more
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            {/* Status */}

                            <div className="statusWidth">
                              <span
                                className={`badgeBooking ${
                                  order.status
                                    ?.toLowerCase()
                                    .replace(
                                      /\s+/g,
                                      "",
                                    )
                                }Badge widthFitContent`}
                              >
                                <span>
                                  {formatStatusBadge(
                                    order.status,
                                  ) || "N/A"}
                                </span>
                              </span>
                            </div>

                            {/* Actions */}

                            <div className="m-0 bookingActionButtons logisticsActionWidth position-relative gap10">

                              {/* View */}

                              <NavLink
                                to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${order?._id}`}
                                style={{
                                  textDecoration:
                                    "none",
                                }}
                              >
                                <p className="m-0">
                                  <img
                                    src="/assets/img/view-filled.svg"
                                    alt=""
                                  />{" "}
                                  {/* View */}
                                </p>
                              </NavLink>

                              {/* Edit */}

                              <p
                                className={`m-0 editButton position-relative ${
                                  userRole ===
                                    "partner" &&
                                  !order?.isAccepted
                                    ? "disbaledEditBooking"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (
                                    userRole ===
                                      "partner" &&
                                    !order?.isAccepted
                                  ) {
                                    return;
                                  }

                                  toggleDropdown(
                                    order._id,
                                  );
                                }}
                              >
                                <img
                                  src="/assets/img/editgrey.svg"
                                  alt=""
                                />{" "}
                                {/* Edit */}
                              </p>

                              {/* Delivery / Work Order */}

                              {isDelivered ? (
                                <div
                                  onClick={() =>
                                    handleDownloadDeliveryOrder(
                                      order,
                                    )
                                  }
                                  style={{
                                    cursor:
                                      "pointer",
                                    padding: "8px", 
                                  }}
                                >
                                  <p className="deliveryOrder m-0">
                                    Delivery order
                                  </p>
                                </div>
                              ) : (
                                <div
                                  onClick={() =>
                                    handleDownloadPdf(
                                      order,
                                    )
                                  }
                                  style={{
                                    cursor:
                                      "pointer",
                                  }}
                                >
                                  <p className="deliveryOrder m-0">
                                    Work order
                                  </p>
                                </div>
                              )}

                              {/* Edit Dropdown */}

                              {activeDropdown ===
                                order._id && (
                                <div
                                  className="actionDropdownOuter"
                                  ref={
                                    dropdownRef
                                  }
                                >
                                  <ul className="actionDropdownList">
                                    {STATUS_OPTIONS.map(
                                      (status) => {
                                        const completedHistory =
                                          order.orderHistory?.filter(
                                            (h) =>
                                              h.completed,
                                          ) || [];

                                        const latestCompleted =
                                          completedHistory[
                                            completedHistory.length -
                                              1
                                          ]?.status.toLowerCase() ||
                                          "";

                                        return (
                                          <li
                                            key={
                                              status
                                            }
                                            className={`actionDropdownItem ${
                                              latestCompleted ===
                                              status.toLowerCase()
                                                ? "selected"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              handleStatusClick(
                                                order._id,
                                                status,
                                              )
                                            }
                                          >
                                            {status.toLowerCase() ===
                                            "in transit"
                                              ? "Other"
                                              : status
                                                  .charAt(
                                                    0,
                                                  )
                                                  .toUpperCase() +
                                                status.slice(
                                                  1,
                                                )}
                                          </li>
                                        );
                                      },
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="noOrders">
                        No order found
                      </p>
                    )}
                  </div>
                </div>

                {/* ========================================= */}
                {/* PROGRESS MODAL */}
                {/* ========================================= */}

                {activeProgress && (
                  <div
                    className="progressModalOverlay"
                    onClick={() =>
                      setActiveProgress(null)
                    }
                  >
                    <div
                      className="progressModal"
                      onClick={(e) =>
                        e.stopPropagation()
                      }
                    >
                      <h4>
                        Order Progress
                      </h4>

                      <ul>
                        {activeProgress.map(
                          (step, i) => (
                            <li key={i}>
                              {step}
                            </li>
                          ),
                        )}
                      </ul>

                      <div className="modalFooter">
                        <button
                          className="btn btn-cancel"
                          onClick={() =>
                            setActiveProgress(
                              null,
                            )
                          }
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========================================= */}
                {/* PAGINATION */}
                {/* ========================================= */}

                {count > 0 &&
                  totalPages > 1 && (
                    <div className="paginationSection">

                      {/* Previous */}

                      <p
                        onClick={() =>
                          currentPage > 1 &&
                          setOffset(
                            currentPage - 1,
                          )
                        }
                        style={{
                          opacity:
                            currentPage > 1
                              ? 1
                              : 0.5,

                          cursor:
                            currentPage > 1
                              ? "pointer"
                              : "default",
                        }}
                      >
                        &lt;
                      </p>

                      {/* Pages */}

                      {generatePagination(
                        currentPage,
                        totalPages,
                      ).map(
                        (page, idx) =>
                          page ===
                          "..." ? (
                            <p key={idx}>
                              ...
                            </p>
                          ) : (
                            <p
                              key={idx}
                              onClick={() =>
                                setOffset(
                                  page,
                                )
                              }
                              style={{
                                fontWeight:
                                  currentPage ===
                                  page
                                    ? "bold"
                                    : "normal",

                                cursor:
                                  "pointer",
                              }}
                            >
                              {page}
                            </p>
                          ),
                      )}

                      {/* Next */}

                      <p
                        onClick={() =>
                          currentPage <
                            totalPages &&
                          setOffset(
                            currentPage + 1,
                          )
                        }
                        style={{
                          opacity:
                            currentPage <
                            totalPages
                              ? 1
                              : 0.5,

                          cursor:
                            currentPage <
                            totalPages
                              ? "pointer"
                              : "default",
                        }}
                      >
                        &gt;
                      </p>

                    </div>
                  )}

                {/* ========================================= */}
                {/* CONFIRMATION MODAL */}
                {/* ========================================= */}

                <ConfirmationModal
                  show={confirmation.show}
                  title={
                    confirmation.statusValue
                      ?.toLowerCase() ===
                    "in transit"
                      ? "Add custom tracking status"
                      : "Update status"
                  }
                  message={
                    confirmation.statusValue
                      ?.toLowerCase() ===
                    "in transit" ? (
                      <div>
                        <p>
                          Please enter a custom tracking status for the user to
                          see:
                        </p>

                        <input
                          type="text"
                          placeholder="Custom tracking status"
                          value={
                            confirmation.customStatus ||
                            ""
                          }
                          onChange={(e) =>
                            setConfirmation({
                              ...confirmation,

                              customStatus:
                                e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            marginBottom: "8px",
                            padding: "6px",
                          }}
                        />

                        <textarea
                          placeholder="Enter here message"
                          value={
                            confirmation.messageText ||
                            ""
                          }
                          onChange={(e) =>
                            setConfirmation({
                              ...confirmation,

                              messageText:
                                e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            padding: "6px",
                          }}
                        />

                        <div
                          style={{
                            border:
                              "2px dashed #d1d5db",

                            borderRadius:
                              "6px",

                            padding: "12px",

                            textAlign:
                              "center",

                            background:
                              "#fafafa",
                          }}
                        >
                          <input
                            type="file"
                            id="attachmentUpload"
                            name="file"
                            ref={fileInputRef}
                            accept="image/*,application/pdf"
                            style={{
                              display:
                                "none",
                            }}
                            onChange={
                              handleFileChange
                            }
                          />

                          <label
                            htmlFor="attachmentUpload"
                            style={{
                              cursor:
                                "pointer",

                              color:
                                "#ff4d2d",

                              fontWeight:
                                "500",
                            }}
                          >
                            Click to upload file
                          </label>

                          <p
                            style={{
                              fontSize:
                                "12px",

                              color: "#888",

                              marginTop:
                                "4px",
                            }}
                          >
                            PNG, JPG, PDF,
                            DOC (Max 5MB)
                          </p>

                          {confirmation?.attachment && (
                            <div
                              style={{
                                marginTop:
                                  "8px",

                                padding:
                                  "6px 10px",

                                background:
                                  "#f1f5f9",

                                borderRadius:
                                  "4px",

                                display:
                                  "flex",

                                justifyContent:
                                  "space-between",

                                alignItems:
                                  "center",
                              }}
                            >
                              <span
                                style={{
                                  fontSize:
                                    "13px",
                                }}
                              >
                                📎{" "}
                                {typeof confirmation.attachment ===
                                "string"
                                  ? confirmation.attachment
                                  : confirmation
                                      .attachment
                                      ?.name}
                              </span>

                              <button
                                type="button"
                                onClick={() => {
                                  setConfirmation(
                                    (prev) => ({
                                      ...prev,

                                      attachment:
                                        null,

                                      uploadedFileName:
                                        "",
                                    }),
                                  );
                                }}
                                style={{
                                  border:
                                    "none",

                                  background:
                                    "transparent",

                                  color: "red",

                                  cursor:
                                    "pointer",
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      `Are you sure you want to update the status to "${
                        confirmation.statusValue
                          ?.toLowerCase()
                          .replace(
                            /_/g,
                            " ",
                          )
                          .charAt(0)
                          .toUpperCase() +
                        confirmation.statusValue
                          ?.toLowerCase()
                          .replace(
                            /_/g,
                            " ",
                          )
                          .slice(1)
                      }"?`
                    )
                  }
                  onConfirm={handleConfirm}
                  onCancel={() =>
                    setConfirmation({
                      show: false,

                      orderId: null,

                      statusValue: "",

                      customStatus: "",

                      messageText: "",

                      attachment: "",

                      uploadedFileName: "",
                    })
                  }
                  confirmText={
                    confirmLoading
                      ? "Confirming..."
                      : confirmation.statusValue
                            ?.toLowerCase() ===
                          "in transit"
                        ? "Save status"
                        : "Confirm"
                  }
                  confirmDisabled={
                    confirmLoading
                  }
                  cancelText="Cancel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }